import type { Scalar } from './models.ts';
import { formatExactNumberFormat } from '../../../src/artifacts/calculation/number-format.mjs';
import type { NumberFormat } from '../../../src/artifacts/presentation-values.mjs';

// Display text for a canonical scalar. A decimal travels as a canonical string
// and is formatted from that string: Intl.NumberFormat accepts a decimal string
// and keeps every digit, so currency never takes a round trip through a JS
// number. Text (a SKU such as 00123) and dates are shown as stored.
//
// A null is never zero or empty text: the caller shows the state it names.
export type ScalarDisplay =
  | { kind: 'text'; text: string }
  | { kind: 'null'; reason: 'source-null' | 'missing' | 'redacted' };

export function formatScalar(
  scalar: Scalar,
  locale: string,
  format?: NumberFormat | null,
): ScalarDisplay {
  if (format && scalar.kind === 'decimal') {
    const typed = formatTyped(scalar, format);
    if (typed !== null) return { kind: 'text', text: typed };
    // The sole typed formatter refused (an engine without exact Intl, or metadata the format
    // does not admit). Re-entering Intl here would be the capability it just rejected, so the
    // stored decimal is shown exactly as it is, with its own currency/unit, unformatted.
    return { kind: 'text', text: storedText(scalar) };
  }
  switch (scalar.kind) {
    case 'null':
      return { kind: 'null', reason: scalar.reason };
    case 'text':
      return { kind: 'text', text: scalar.value };
    case 'date':
      return { kind: 'text', text: scalar.value };
    case 'boolean':
      return { kind: 'text', text: String(scalar.value) };
    case 'decimal': {
      const currency = scalar.currency;
      const digits = { minimumFractionDigits: scalar.scale, maximumFractionDigits: scalar.scale };
      const formatter = new Intl.NumberFormat(locale, {
        ...digits,
        ...(currency ? { style: 'currency', currency } : {}),
        roundingMode: scalar.rounding === 'half-even' ? 'halfEven' : 'halfExpand',
      } as Intl.NumberFormatOptions);
      const text = formatter.format(scalar.value as unknown as number);
      return { kind: 'text', text: scalar.unit && !currency ? `${text} ${scalar.unit}` : text };
    }
  }
}

// The canonical decimal string and its declared metadata, with no numeric formatting.
function storedText(scalar: Extract<Scalar, { kind: 'decimal' }>) {
  const label = scalar.currency ?? scalar.unit;
  return label ? `${scalar.value} ${label}` : scalar.value;
}

// A typed number format from the canonical presentation, by EXP's one exact formatter
// (the decimal string reaches Intl whole; metadata and rounding are checked). When it
// refuses (an engine without exact Intl, or metadata the format does not admit) the stored
// decimal is shown as stored (storedText): exact, never a plausible lossy preview.
function formatTyped(scalar: Extract<Scalar, { kind: 'decimal' }>, format: NumberFormat) {
  try {
    return formatExactNumberFormat(scalar, format);
  } catch {
    return null;
  }
}
